/* eslint-disable no-console */
/* eslint-disable camelcase */
import { MediaBackground } from '../stores/reducers/content';
import { ContentServiceJobStatus } from '../stores/types/content';
import {
	MediaItem,
	PageDeleted,
	PagedMediaItemNew,
	PagedMediaItemExisting,
} from '../stores/reducers/media';
import { UPLOAD_STATUS } from '../types/constants';
import { CloudConvertUploadData, Entity } from '../types/interfaces';
import { createInput } from './upload-document';
import { getAuthenticatedRequestInit } from './utils';
import { appLogger } from '../utils/datadog';
import { EntityType } from '../types/enums';
import { getEmbedSafeHeaders } from './auth';
import blpAuth from './blp-auth';

export const S3_SUB_DIRECTORY = {
	THUMBS: 'thumbs',
	POSTER: 'poster',
};

export const CreateMedia = (token: string, media: MediaItem) => {
	const {
		original_name,
		meeting,
		created_by,
		type,
		cloud_convert_id,
		convert_status,
		endpoint,
		images,
		template_type = null,
		provider = null,
		media_data
	} = media;
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/create`, {
		method: 'POST',
		body: JSON.stringify({
			original_name,
			meeting,
			created_by,
			type,
			cloud_convert_id,
			endpoint,
			images,
			convert_status: convert_status ? convert_status : ContentServiceJobStatus.processing,
			template_type,
			provider,
			media_data
		}),
		...getAuthenticatedRequestInit(token),
	})
		.then((result) => {
			if (result.ok) {
				return result.json();
			}
			throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/create could not save`);
		});
};


export const CreateMediaFromGenerateContentSound = (token: string, meetingId: string, trackId: string, versionId: string, mediaId?: number) => {
	let data: any = {
		trackId,
		versionId,
		meetingId
	};

	if (mediaId)
		data.mediaId = mediaId;

	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/generated-content/sound/track/media`, {
		method: 'POST',
		body: JSON.stringify(data),
		...getAuthenticatedRequestInit(token),
	})
		.then((result) => {
			if (result.ok) {
				return result.json();
			}
			throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/generated-content/sound/track/media could not save`);
		});
};


export const GetMedia = (token: string, meeting: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/${meeting}`, {
	method: 'GET',
	...getAuthenticatedRequestInit(token),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/${meeting}`);
});

export const SaveMediaItem = (
	token: string,
	mediaItem: MediaItem,
	pageDeleted: PageDeleted | undefined,
) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/save-item`, {
	method: 'POST',
	body: JSON.stringify({
		media: mediaItem,
		pageDeleted,
	}),
	...getAuthenticatedRequestInit(token),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/save-item could not save`);
});

export const DeleteMediaItem = (token: string, id: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/${id}`, {
	method: 'DELETE',
	...getAuthenticatedRequestInit(token),
})
	.then((result) => {
		if (!result.ok) {
			throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/delete didn't work`);
		}
	});

export const GetConvertedMedia = (token: string, cloud_convert_id: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/converted/${cloud_convert_id}`, {
	method: 'GET',
	...getAuthenticatedRequestInit(token),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/converted didn't work`);
});

export const GetCloudConvertJob = (
	token: string,
	ext: string,
	prefix: string,
	filename?: string,
	tags?: string,
): Promise<CloudConvertUploadData> => {
	const includeFileName = filename ? `&file=${filename}` : '';
	const includeTags = filename ? `&tags=${tags}` : '';
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/convert/${ext}?ext=${ext}&prefix=${encodeURIComponent(prefix)}${includeFileName}${includeTags}`, {
		method: 'GET',
		...getAuthenticatedRequestInit(token),
	}).then((result) => {
		if (result.ok) {
			return result.json();
		}
		throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/convert/${ext} didn't work`);
	});
};

export const ConvertMovToOverlay = (
	token: string,
	prefix: string,
	url: string,
	tags: string,
	meetingId: number,
): Promise<CloudConvertUploadData> => {
	const prefixQuery = `prefix=${encodeURIComponent(prefix)}`;
	const urlQuery = `url=${encodeURIComponent(url.replace('-mov-hls/master.m3u8', '.mov'))}`;
	const tagQuery = `tags=${tags}`;

	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/convert-mov/${meetingId}?${prefixQuery}&${urlQuery}&${tagQuery}`, {
		method: 'GET',
		...getAuthenticatedRequestInit(token),
	}).then((result) => {
		if (result.ok) {
			return result.json();
		}
		throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/convert-mov didn't work`);
	});
};

export const GetUploadEndpoint = async (token: string, prefix: string, filename: string, subdirectory?: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/upload`, {
	method: 'POST',
	body: JSON.stringify({
		filename,
		prefix,
		subdirectory,
	}),
	...getAuthenticatedRequestInit(token),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/upload didn't work`);
});

export interface onHttpUploadInitFunction { (request: XMLHttpRequest): void; }
export interface onUploadProgressFunction { (total: number): void; }
export const UploadToS3 = async (
	url: string,
	file: File | Blob,
	data: any,
	filename: string,
	onHttpUploadInit?: onHttpUploadInitFunction,
) => {
	const formData = new FormData();
	// eslint-disable-next-line guard-for-in, no-restricted-syntax
	for (const key in data) {
		formData.append(key, data[key]);
	}
	formData.append('file', file, filename);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	return new Promise((resolve, reject) => {
		const request = new XMLHttpRequest();
		onHttpUploadInit?.(request);
		request.open('POST', url);
		request.onload = () => resolve(UPLOAD_STATUS.LOAD);
		request.onabort = () => resolve(UPLOAD_STATUS.ABORT);
		request.onerror = () => resolve(UPLOAD_STATUS.ERROR);
		request.send(formData);
	});
};

export const UploadCloudConvert = async (
	cloudConvertUpload: any,
	file: FileList,
	onHttpUploadInit: onHttpUploadInitFunction,
): Promise<string> => {
	const form = document.createElement('form');
	form.action = cloudConvertUpload.form.url;
	form.enctype = 'multipart/form-data';
	form.method = 'post';

	// The form parameters returned from createCloudConvertJob() are dynamic so we build this upload
	// form dynamically using whatever parameters they gives us
	// (these can and have changed in the past)
	const { parameters } = cloudConvertUpload.form;
	// eslint-disable-next-line guard-for-in, no-restricted-syntax
	for (const key in parameters) {
		// eslint-disable-next-line no-prototype-builtins
		if (parameters.hasOwnProperty(key)) {
			form.appendChild(createInput(key, 'hidden', parameters[key]));
		}
	}
	// File is always last
	form.appendChild(createInput('file', 'file', file));
	const submit = document.createElement('input');
	submit.type = 'submit';
	form.appendChild(submit);

	const formData = new FormData(form);

	return new Promise((resolve, reject) => {
		try {
			const request = new XMLHttpRequest();
			onHttpUploadInit?.(request);
			request.open('POST', cloudConvertUpload.form.url);
			request.onload = () => resolve(UPLOAD_STATUS.LOAD);
			request.onabort = () => resolve(UPLOAD_STATUS.ABORT);
			request.onerror = () => resolve(UPLOAD_STATUS.ERROR);
			request.send(formData);
		} catch (e) {
			// eslint-disable-next-line prefer-promise-reject-errors
			reject(false);
		}
	});
};

export const ChangeSlideOrder = (token: string, id: number, imageString: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/change-slide-order`, {
	method: 'POST',
	body: JSON.stringify({
		id,
		imageString,
	}),
	...getAuthenticatedRequestInit(token),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/upload/finish didn't work`);
});

export const DeleteCloudConvertJob = (token: string, cloud_convert_id: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/cloud-convert/${cloud_convert_id}`, {
	method: 'DELETE',
	...getAuthenticatedRequestInit(token),
})
	.then((result) => {
		if (!result.ok) {
			throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/cloud-convert/delete didn't work`);
		}
	});

export const GetUserMediaCanvasBackgrounds = (token: string, meeting: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/canvas-backgrounds?meeting=${meeting}`, {
	method: 'GET',
	...getAuthenticatedRequestInit(token),
})
	.then((result) => {
		if (result.ok) {
			return result.json();
		}
		throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/canvas-backgrounds get didn't work`);
	});

export const UploadUserMediaCanvasBackground = (token: string, gr_user: number, meeting: number, url: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/canvas-backgrounds`, {
	method: 'POST',
	body: JSON.stringify({
		gr_user,
		meeting,
		url,
	}),
	...getAuthenticatedRequestInit(token),
})
	.then((result) => {
		if (!result.ok) {
			throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/canvas-backgrounds upload didn't work`);
		}
	});

export const GetUserMediaCanvasWallpapers = (token: string, meeting: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/canvas-wallpapers?meeting=${meeting}`, {
	method: 'GET',
	...getAuthenticatedRequestInit(token),
})
	.then((result) => {
		if (result.ok) {
			return result.json();
		}
		throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/canvas-wallpapers get didn't work`);
	});

export const UploadUserMediaCanvasWallpaper = (token: string, gr_user: number, meeting: number, url: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/canvas-wallpapers`, {
	method: 'POST',
	body: JSON.stringify({
		gr_user,
		meeting,
		url,
	}),
	...getAuthenticatedRequestInit(token),
})
	.then((result) => {
		if (!result.ok) {
			throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/canvas-wallpapers upload didn't work`);
		}
	});

export const GetVirtualBgs = (
	token: string,
	userId: number,
) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/virtual-bg?userId=${userId}`, {
	method: 'GET',
	...getAuthenticatedRequestInit(token),
})
	.then((result) => {
		if (result.ok) {
			return result.json();
		}
		throw new Error(`Could not get virtual backgrounds for user ${userId}`);
	});

export const UploadVirtualBg = (
	token: string,
	gr_user: number,
	meeting: number,
	url: string,
) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/virtual-bg`, {
	method: 'POST',
	body: JSON.stringify({
		gr_user,
		meeting,
		url,
	}),
	...getAuthenticatedRequestInit(token),
})
	.then((result) => {
		if (!result.ok) {
			throw new Error(`Could not upload virtual background for meeting ${meeting}`);
		}
	});

export const DeleteUserMedia = (token: string, id: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/${id}`, {
	method: 'DELETE',
	...getAuthenticatedRequestInit(token),
})
	.then((result) => {
		if (!result.ok) {
			throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-media/ delete didn't work`);
		}
	});

export const GetDefaultBackgrounds = (token: string): Promise<MediaBackground[]> => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/all-meetings/backgrounds`, {
	method: 'GET',
	...getAuthenticatedRequestInit(token),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.error('Unable to load default meeting backgrounds.');

	return [];
});

export const EditRecordingMetadata = (token: string, streamId: string, broadcastStartedBy: number, broadcastStartingShotId: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/recording/${streamId}`, {
	method: 'PUT',
	body: JSON.stringify({
		createdByUserId: broadcastStartedBy,
		startingShotId: broadcastStartingShotId,
	}),
	...getAuthenticatedRequestInit(token),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.error('Unable to edit recording metadata.');

	return [];
});

export const FetchSingleMediaItem = (
	token: string,
	meeting: number,
	mediaId: number,
) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/media/${meeting}/${mediaId}`, {
	method: 'GET',
	...getAuthenticatedRequestInit(token),
}).then(async (result) => {
	if (result.ok) {
		const itemArr = await result.json();
		return itemArr[0] ?? null;
	}
	console.error('Unable to fetch media item.');

	return null;
});

export const GetMediaPagesForMeeting = (
	token: string,
	meetingNumber: number,
): Promise<PagedMediaItemExisting[] | null> => (
	fetch(`${process.env.REACT_APP_API_HOST}/v3/greenroom/${meetingNumber}/media-page`, {
		method: 'GET',
		...getAuthenticatedRequestInit(token),
	}).then(async (result: Response) => {
		if (result.ok) {
			const pagedMediaItemArray = await result.json();

			return pagedMediaItemArray;
		}

		console.error('Unable to get paged media items.');

		return null;
	})
);

export const SaveMediaPage = (
	token: string,
	meetingNumber: number,
	pagedMediaItem: PagedMediaItemNew | PagedMediaItemExisting,
	from: string,
): Promise<PagedMediaItemExisting | null> => (
	fetch(`${process.env.REACT_APP_API_HOST}/v3/greenroom/${meetingNumber}/media-page`, {
		method: 'POST',
		body: JSON.stringify({
			pagedMediaItem,
			from,
		}),
		...getAuthenticatedRequestInit(token),
	}).then(async (result: Response) => {
		if (result.ok) {
			const upsertedMediaItem = await result.json();

			return upsertedMediaItem;
		}

		console.error('Unable to save paged media item.');

		return null;
	})
);

export const GetRecentSlideDeck = async (
	room: string | number,
): Promise<Entity | null> => blpAuth.get({
	url: `/v1/room/${room}/recent-slide-deck`,
	host: process.env.REACT_APP_SYNC_HOST,
	token: blpAuth.accessToken ?? undefined,
	options: { headers: getEmbedSafeHeaders() },
}).catch((err) => {
	appLogger.error('error getting recent slide deck: ', err);
	return null;
}).then((res) => {
	if (res?.entityType && res.entityType === EntityType.recentSlideDeck) {
		return res;
	}
	return null;
});

export const UpsertRecentSlideDeck = async (
	room: number,
	mediaId: number,
	version?: number,
): Promise<boolean> => blpAuth.put({
	url: `/v1/room/${room}/recent-slide-deck`,
	host: process.env.REACT_APP_SYNC_HOST,
	token: blpAuth.accessToken ?? undefined,
	options: { headers: getEmbedSafeHeaders() },
	body: { mediaId, version },
}).then((res) => {
	if (res) return true;
	console.error('unable to upsert recent slide deck entity');
	return false;
}).catch((e) => {
	appLogger.error('error upserting recent slide deck: ', e);
	return false;
});

export const UpsertMediaDraft = async (
	room: number,
	media: MediaItem,
	isInit: boolean = false,
): Promise<boolean> => blpAuth.put({
	url: `/v1/room/${room}/media/${media.id}/draft`,
	host: process.env.REACT_APP_SYNC_HOST,
	token: blpAuth.accessToken ?? undefined,
	options: { headers: getEmbedSafeHeaders() },
	body: { media, isInit },
}).then((res) => {
	if (res) return true;
	console.error('unable to upsert media draft entity');
	return false;
}).catch((e) => {
	appLogger.error('error upserting media draft: ', e);
	return false;
});